import type { Nftcollection } from '@/types';

export interface CatalogTemplateProps {
  slug: string;
  subSlug?: string;
}

export type CatalogType =
  | 'all'
  | 'artwork'
  | 'collection'
  | 'drop'
  | 'oneofone'
  | 'installation'
  | 'physical'
  | 'immersive'
  | 'exhibition'
  | 'collaboration'
  | 'writing'
  | 'award';

export type SortType = 'latest' | 'oldest' | 'title_asc' | 'title_desc';

/**
 * Common types for PayloadCMS API
 */

export interface BaseQueryParams {
  catalogType?: CatalogType;
  page?: number;
  limit?: number;
  where?: Record<string, unknown>;
  select?: Record<string, boolean>;
  depth?: number;
  sort?: string;
  query?: string;
  tag?: string;
  category?: string;
}

// API parameter types for NFT collections
export interface FindNftCollectionsParams extends BaseQueryParams {
  // Filter parameters
  years?: string[];
  medium?: string[];
  platform?: string[];
  chains?: string[];
  type?: string[];
  collectionid?: number;
  getChildCollections?: boolean;
  parentid?: number;
  oneofone?: boolean;
}

// API parameter types for installations
export interface FindInstallationsParams extends BaseQueryParams {
  // Filter parameters
  years?: string[];
  type?: string[];
}

// Valid values for PayloadCMS API sort parameter
export type PayloadSortParam =
  | keyof Nftcollection
  | `-${string & keyof Nftcollection}`
  | '-viewCount' // Special sort fields
  | 'viewCount'
  | '-createdAt'
  | 'createdAt'
  | 'name'
  | '-name'
  | undefined;

/**
 * PayloadCMS query operators for where clauses
 * Used across all catalog services for type-safe queries
 */
export type QueryOperator = {
  equals?: string | number | boolean;
  not_equals?: string | number | boolean;
  contains?: string;
  in?: (string | number)[];
  not_in?: (string | number)[];
  greater_than?: string | number;
  greater_than_equal?: string | number;
  less_than?: string | number;
  less_than_equal?: string | number;
  exists?: boolean;
  between?: string[];
  like?: string;
};

/**
 * PayloadCMS where filter type
 * Supports nested and/or conditions
 */
export type WhereFilter = {
  [key: string]: QueryOperator | WhereCondition | WhereFilter[] | undefined;
};

export type WhereCondition = {
  and?: WhereFilter[];
  or?: WhereFilter[];
};

export interface PaginatedResponse<T> {
  docs: T[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

export interface APIErrorResponse {
  statusCode: number;
  message: string;
  errors?: Record<string, string[]>;
}

// Common constants
export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_SORT = '-updatedAt';

// Filter types
export interface FilterItem {
  label: string;
  value: string;
  suffix?: string;
}

export interface Phase {
  name?: string | null;
  time?: string | null;
  supply?: number | null;
  price?: number | null;
  count?: number | null;
  collectors?: unknown[] | null;
  walletAddresses?: string | null;
  id?: string | null;
}

export interface FilterCategory {
  title: string;
  key: string;
  items: FilterItem[];
}

export interface SelectedFiltersType {
  medium: string[];
  category: string[];
  chains: string[];
  years: string[];
  type: string[];
  platform: string[];
  [key: string]: string[];
}

// Catalog item types
export interface CatalogItem {
  id: string | number;
  slug?: string;
  href?: string;
  type: CatalogType;
  title: string;
  displayTitle: string;
  filename: string;
  thumbnail: string;
  imageUrl: string;
  width: number;
  height: number;
  date?: string;
  year?: number;
  description?: string;
  longDescription?: string;
  medium?: string;
  dimensions?: string;
  location?: string;
  tags: string[];
  detailTags?: (string | { title: string; url: string })[];
  // Legacy properties
  artist?: string;
  price?: number;
  available?: boolean;
  walletAddress?: string;
  duration?: string;
  videoUrl?: string;
  htmlUrl?: string; // URL for HTML files
  collections?: string[];
  artworkCount?: number;
  collectorCount?: number;
  categories?: string[];
  category?: string;
  // Additional fields for writings
  doi?: string;
  authors?: string[];
  publishedIn?: string;
  pdfUrl?: string;
  originalLink?: string;
  // Drop-specific fields
  dropStatus?: 'draft' | 'published' | 'completed' | null;
  dropType?: 'series' | 'oneofone' | null;
  publishType?: 'hosted' | 'partner' | null;
  startDate?: string | null;
  endDate?: string | null;
  revealDateTime?: string | null;
  publicPrice?: number | null;
  totalSupply?: number | null;
  contractAddress?: string | null;
  /** True when NFTCollection.status = "upcoming". Drops use NFTCollections, not separate Drops collection. */
  isDrop?: boolean;
  marketname?: string;
  marketLink?: string;
  // Additional fields for artwork details
  traits?:
    | {
        traitType?: string | null;
        value?: string | null;
        rank?: number | null;
        id?: string | null;
      }[]
    | null;
  fileSize?: number;
  fileFormat?: string;
  resolution?: string;

  currentPrice?: number | null;
  lastSalePrice?: number | null;
  phases?: Phase[] | null;
  symbol?: string;
  marketUrl?: string | null;
  totalSales?: number | null;
  currentOwnerAddress?: string | null;
  lastSaleDate?: string | null;

  // Aspect ratio (priority: artwork > collection > calculated)
  artworkAspectRatio?: string | null;

  // Hover transition type for product cards
  hoverTransition?: 'crossfade-scale' | 'blur-dissolve';

  // Series/Collection info for tree view grouping
  seriesId?: number | null;
  seriesTitle?: string | null;
  seriesSlug?: string | null;
  seriesHref?: string | null;
  seriesYear?: number | null;
}

/**
 * Sort configuration for catalog services
 */
export interface SortConfig {
  /** The date field to use for latest/oldest sorting (e.g., 'createdAt', 'publishDate') */
  dateField: string;
  /** The title field to use for title sorting (default: 'title') */
  titleField?: string;
  /** Default sort value if no sort option is provided */
  defaultSort?: string;
}

/**
 * UI sort options that can be passed from the frontend
 */
export type UISortOption =
  | 'latest'
  | 'newest'
  | 'oldest'
  | 'title_asc'
  | 'title_desc'
  | 'a-z'
  | 'z-a'
  | 'rand'
  | 'rand_asc'
  | 'rand_desc';

/**
 * Convert UI sort option to PayloadCMS API sort value
 *
 * @param sort - UI sort option from frontend
 * @param config - Sort configuration with date field for the entity type
 * @returns API sort value string
 *
 * @example
 * // For writings (uses publishDate)
 * getSortValue('latest', { dateField: '-publishDate' }) // returns '-publishDate'
 *
 * // For exhibitions (uses duration.startDate)
 * getSortValue('oldest', { dateField: '-duration.startDate' }) // returns 'duration.startDate'
 */
export function getSortValue(sort: string | undefined, config: SortConfig): string {
  const { dateField, titleField = 'title', defaultSort } = config;

  // Extract base field name (remove leading '-' if present)
  const baseField = dateField.startsWith('-') ? dateField.slice(1) : dateField;

  switch (sort) {
    case 'latest':
    case 'newest':
      return `-${baseField}`;
    case 'oldest':
      return baseField;
    case 'title_asc':
    case 'a-z':
      return titleField;
    case 'title_desc':
    case 'z-a':
      return `-${titleField}`;
    case 'rand':
    case 'rand_asc':
      return 'rand';
    case 'rand_desc':
      return '-rand';
    default:
      return defaultSort || `-${baseField}`;
  }
}
